/**
 * WardrobePhotoGuideModal — Guide photo affiché À CHAQUE ajout d'article (par
 * défaut). L'utilisateur peut cocher "Ne plus afficher" pour passer cette
 * étape lors des prochains ajouts.
 *
 * Objectif : aider à prendre des photos optimales pour Claude AI Vision.
 * Une photo floue / mal cadrée peut être REJETÉE → l'analyse IA est consommée
 * dans le quota quotidien sans rien apporter.
 *
 * Persistance : flag AsyncStorage `wardrobe_photo_guide_optout_${userId}` posé
 * à true seulement si l'utilisateur coche "Ne plus afficher" + valide.
 */

import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, Image, StyleSheet,
} from 'react-native';
import AppModal from './AppModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useI18n } from '../i18n';

const C = {
  bg: '#FAF8F4', card: '#FFFFFF', text: '#1A1A1A',
  textMuted: '#6B6B6B', textFaint: '#A0A0A0',
  border: '#EAE6DF', gold: '#D4AF72', goldDark: '#B8860B',
  good: '#4CAF50', bad: '#E53935', warn: '#F59E0B',
};

// Images générées par Imagen 4 (style premium luxe Mytenue, fond blanc, ghost
// mannequin, 1 article seul) — hébergées sur le bucket public S3.
// Format clé : flat-lay/dressing-guide/{F|M}_{category}.jpg
const GUIDE_BUCKET = 'https://outfit-tryon-cache.s3.eu-west-3.amazonaws.com/flat-lay/dressing-guide';

function buildGuideUrl(category, gender) {
  const g = gender === 'M' ? 'M' : 'F';
  return `${GUIDE_BUCKET}/${g}_${category}.jpg`;
}

// Catégories adaptées par genre.
// Femme : 9 cats (Hauts, Bas, Robes, Vestes, Manteaux, Chaussures, Sacs, Bijoux, Accessoires)
// Homme : 8 cats (Hauts, Bas, Vestes, Manteaux, Chaussures, Sacs, Montres, Accessoires) — pas de Robes
//
// `imageKey` correspond directement aux images Imagen générées sur S3 :
// flat-lay/dressing-guide/{F|M}_{imageKey}.jpg
function getGuideCategories(gender, en) {
  const isMale = gender === 'M';
  const common = [
    { imageKey: 'Hauts', label: en ? 'Tops' : 'Hauts', fallbackEmoji: '👕',
        tip: isMale
          ? (en ? 'Shirt on a ghost mannequin (no face). Sleeves unfolded, collar visible.' : 'Chemise sur ghost mannequin (sans visage). Manches dépliées, col visible.')
          : (en ? 'Shirt/blouse on a ghost mannequin (no face). Sleeves unfolded, collar visible.' : 'Chemise/blouse sur ghost mannequin (sans visage). Manches dépliées, col visible.') },
    { imageKey: 'Bas', label: en ? 'Bottoms' : 'Bas', fallbackEmoji: '👖',
        tip: en ? 'ONE single pair of trousers/jeans laid flat on white background. Legs aligned.' : 'UN seul pantalon/jean posé à plat sur fond blanc. Jambes alignées.' },
  ];
  const middle = isMale
    ? [
        { imageKey: 'Vestes', label: en ? 'Jackets' : 'Vestes', fallbackEmoji: '🧥',
            tip: en ? 'SHORT jacket/blazer (< hip) on ghost mannequin without face.' : 'Veste/blazer COURT (< hanche) sur ghost mannequin sans visage.' },
        { imageKey: 'Manteaux', label: en ? 'Coats' : 'Manteaux', fallbackEmoji: '🧥',
            tip: en ? 'LONG coat (≥ mid-thigh) on ghost mannequin without face.' : 'Manteau LONG (≥ mi-cuisse) sur ghost mannequin sans visage.' },
      ]
    : [
        { imageKey: 'Robes', label: en ? 'Dresses' : 'Robes', fallbackEmoji: '👗',
            tip: en ? 'Dress on ghost mannequin without face. Full length visible.' : 'Robe sur ghost mannequin sans visage. Toute la longueur visible.' },
        { imageKey: 'Vestes', label: en ? 'Jackets' : 'Vestes', fallbackEmoji: '🧥',
            tip: en ? 'SHORT jacket/blazer (< hip) on ghost mannequin without face.' : 'Veste/blazer COURT (< hanche) sur ghost mannequin sans visage.' },
        { imageKey: 'Manteaux', label: en ? 'Coats' : 'Manteaux', fallbackEmoji: '🧥',
            tip: en ? 'LONG coat (≥ mid-thigh) — trench, peacoat, parka — on ghost mannequin.' : 'Manteau LONG (≥ mi-cuisse) — trench, caban, parka — sur ghost mannequin.' },
      ];
  const tail = [
    { imageKey: 'Chaussures', label: en ? 'Shoes' : 'Chaussures', fallbackEmoji: isMale ? '👞' : '👠',
        tip: en ? 'A single shoe shown in profile on white background.' : 'Une seule chaussure de profil sur fond blanc.' },
    { imageKey: 'Sacs', label: en ? 'Bags' : 'Sacs', fallbackEmoji: isMale ? '🧳' : '👜',
        tip: en ? 'Bag alone, placed upright. Handles or strap visible.' : 'Sac seul, posé droit. Anses ou bandoulière visibles.' },
    { imageKey: 'Bijoux',
        label: isMale ? (en ? 'Watches' : 'Montres') : (en ? 'Jewelry' : 'Bijoux'),
        fallbackEmoji: isMale ? '⌚' : '💍',
        tip: en ? 'Close-up on white background. Details and finish visible.' : 'Plan rapproché sur fond blanc. Détails et finition visibles.' },
    { imageKey: 'Accessoires', label: en ? 'Accessories' : 'Accessoires', fallbackEmoji: isMale ? '👔' : '🧣',
        tip: isMale
          ? (en ? 'Tie / scarf / belt alone, laid out on white background.' : 'Cravate / écharpe / ceinture seule, étalée sur fond blanc.')
          : (en ? 'Scarf / hat / belt alone, laid out on white background.' : 'Écharpe / chapeau / ceinture seule, étalée sur fond blanc.') },
  ];
  return [...common, ...middle, ...tail];
}

const RULES_OK_FR = [
  'Fond uni clair (mur blanc, drap, papier)',
  'Lumière naturelle ou plafonnier blanc',
  'Un seul article dans le cadre',
  'Photo prise droit (pas en biais)',
  'Article entier visible (pas coupé)',
  'Article porté possible : il doit rester le sujet principal, entièrement visible et non masqué par d\'autres vêtements ou accessoires (visage hors cadre conseillé)',
];

const RULES_OK_EN = [
  'Plain light background (white wall, sheet, paper)',
  'Natural light or white overhead light',
  'Only one item in the frame',
  'Take the photo straight on (not at an angle)',
  'Entire item visible (not cropped)',
  'Worn items allowed: the item must remain the main subject, fully visible and not covered by other clothing or accessories (face out of frame recommended)',
];

const RULES_BAD_FR = [
  'Plusieurs articles superposés sur la photo',
  'Article masqué par une veste, un sac ou un autre vêtement',
  'Cintres apparents, étiquettes ou packaging visibles',
  'Fond très chargé ou avec motifs',
  'Photo très sombre, floue ou de mauvaise qualité',
];

const RULES_BAD_EN = [
  'Multiple items stacked in the same photo',
  'Item hidden by a jacket, bag or another piece of clothing',
  'Visible hangers, tags or packaging',
  'Very busy or patterned background',
  'Very dark, blurry or low-quality photo',
];

// Sous-composant : une cell exemple avec fallback emoji si l'image Unsplash
// ne charge pas (404, CDN bloqué, hors ligne…).
function GuideExampleCell({ item }) {
  const [imgError, setImgError] = useState(false);
  return (
    <View style={styles.gridCell}>
      {imgError ? (
        <View style={[styles.gridImg, styles.gridFallback]}>
          <Text style={{ fontSize: 48 }}>{item.fallbackEmoji}</Text>
        </View>
      ) : (
        <Image
          source={{ uri: item.image }}
          style={styles.gridImg}
          resizeMode="cover"
          onError={() => setImgError(true)}
        />
      )}
      <Text style={styles.gridLabel}>{item.category}</Text>
      <Text style={styles.gridTip}>{item.tip}</Text>
    </View>
  );
}

export default function WardrobePhotoGuideModal({ visible, onContinue, onClose, gender = 'F' }) {
  const insets = useSafeAreaInsets();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { lang } = useI18n();
  const en = lang === 'en';

  const handleContinue = () => {
    onContinue?.(dontShowAgain);
    setDontShowAgain(false);
  };

  const RULES_OK = en ? RULES_OK_EN : RULES_OK_FR;
  const RULES_BAD = en ? RULES_BAD_EN : RULES_BAD_FR;

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      webMaxWidth={560}
      webBackdropClosable
    >
      <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: Math.max(insets.top, 20) }}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>📸 {en ? 'Optimal photos' : 'Photos optimales'}</Text>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Text style={{ fontSize: 26, color: C.textMuted, fontWeight: '300' }}>×</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 24 + Math.max(insets.bottom, 12) }}>
          <Text style={styles.intro}>
            {en
              ? 'For our AI to correctly recognize your items (category, color, material, style), take clear and sharp photos.'
              : 'Pour que notre IA reconnaisse correctement vos articles (catégorie, couleur, matière, style), prenez des photos claires et nettes.'}
          </Text>

          {/* Warning IA rejet */}
          <View style={styles.warnBox}>
            <Text style={styles.warnTitle}>{en ? '⚠️ Important' : '⚠️ Important'}</Text>
            <Text style={styles.warnText}>
              {en ? (
                <>If the photo is blurry, badly framed or non-compliant, the AI may <Text style={{ fontWeight: '700' }}>reject the analysis</Text>. You will then lose <Text style={{ fontWeight: '700' }}>1 use of your daily AI analyses counter</Text> without any item being added to your wardrobe.</>
              ) : (
                <>Si la photo est floue, mal cadrée ou non conforme, l'IA peut <Text style={{ fontWeight: '700' }}>rejeter l'analyse</Text>. Vous perdrez alors <Text style={{ fontWeight: '700' }}>1 utilisation de votre compteur d'analyses IA quotidien</Text> sans avoir d'article ajouté à votre dressing.</>
              )}
            </Text>
          </View>

          {/* Règles OK */}
          <View style={[styles.ruleBlock, { borderLeftColor: C.good }]}>
            <Text style={[styles.ruleTitle, { color: C.good }]}>{en ? '✓ DO' : '✓ À FAIRE'}</Text>
            {RULES_OK.map((r, i) => (
              <Text key={i} style={styles.ruleText}>• {r}</Text>
            ))}
          </View>

          {/* Règles BAD */}
          <View style={[styles.ruleBlock, { borderLeftColor: C.bad }]}>
            <Text style={[styles.ruleTitle, { color: C.bad }]}>{en ? '✗ AVOID' : '✗ À ÉVITER'}</Text>
            {RULES_BAD.map((r, i) => (
              <Text key={i} style={styles.ruleText}>• {r}</Text>
            ))}
          </View>

          {/* Examples par catégorie — photos générées Imagen + fallback emoji */}
          <Text style={styles.sectionTitle}>{en ? 'EXAMPLES BY CATEGORY' : 'EXEMPLES PAR CATÉGORIE'}</Text>
          <View style={styles.grid}>
            {getGuideCategories(gender, en).map((g, i) => (
              <GuideExampleCell
                key={i}
                item={{ ...g, category: g.label, image: buildGuideUrl(g.imageKey, gender) }}
              />
            ))}
          </View>

          <View style={styles.aiHint}>
            <Text style={styles.aiHintTitle}>{en ? '🤖 Why it matters' : '🤖 Pourquoi c\'est important'}</Text>
            <Text style={styles.aiHintText}>
              {en
                ? "A good photo allows the AI to automatically detect the category, dominant color, material and style — saving you from entering everything manually and improving the quality of outfit suggestions."
                : "Une bonne photo permet à l'IA de détecter automatiquement la catégorie, la couleur dominante, la matière et le style — vous évitant de tout saisir manuellement et améliorant la qualité des suggestions de tenues."}
            </Text>
          </View>

          {/* Checkbox "ne plus afficher" */}
          <TouchableOpacity
            onPress={() => setDontShowAgain(v => !v)}
            activeOpacity={0.85}
            style={styles.checkboxRow}
          >
            <View style={[styles.checkbox, dontShowAgain && styles.checkboxChecked]}>
              {dontShowAgain && <Text style={styles.checkboxMark}>✓</Text>}
            </View>
            <Text style={styles.checkboxLabel}>{en ? "Don't show this guide again" : 'Ne plus afficher ce guide'}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={handleContinue}
            activeOpacity={0.85}
            style={styles.cta}
          >
            <Text style={styles.ctaText}>{en ? 'GOT IT, ADD AN ITEM' : "J'AI COMPRIS, AJOUTER UN ARTICLE"}</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    </AppModal>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 16, borderBottomWidth: 1, borderBottomColor: C.border,
  },
  title: { fontSize: 16, fontWeight: '700', color: C.text },
  intro: { fontSize: 13, color: C.textMuted, lineHeight: 19, marginBottom: 14 },
  warnBox: {
    backgroundColor: '#FFF8E6',
    borderWidth: 1, borderColor: '#F0DCA0',
    borderRadius: 8, padding: 12, marginBottom: 14,
  },
  warnTitle: { fontSize: 12, fontWeight: '800', color: '#92580E', marginBottom: 4, letterSpacing: 0.3 },
  warnText: { fontSize: 12, color: '#6B4F1E', lineHeight: 17 },
  ruleBlock: {
    backgroundColor: C.card, borderLeftWidth: 3,
    paddingLeft: 12, paddingRight: 12, paddingVertical: 10,
    borderRadius: 6, marginBottom: 12,
  },
  ruleTitle: { fontSize: 11, fontWeight: '800', letterSpacing: 1, marginBottom: 6 },
  ruleText: { fontSize: 12, color: C.text, lineHeight: 18 },
  sectionTitle: {
    fontSize: 11, fontWeight: '800', color: C.textMuted,
    letterSpacing: 1, marginTop: 12, marginBottom: 8,
  },
  grid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  gridCell: {
    width: '48%', backgroundColor: C.card, borderRadius: 10,
    borderWidth: 1, borderColor: C.border,
    padding: 10, marginBottom: 10, alignItems: 'center',
  },
  gridImg: {
    width: '100%', aspectRatio: 1, borderRadius: 8,
    backgroundColor: C.bg, marginBottom: 8,
  },
  gridFallback: {
    alignItems: 'center', justifyContent: 'center',
  },
  gridLabel: {
    fontSize: 12, fontWeight: '700', color: C.text,
    letterSpacing: 0.3, marginBottom: 4,
  },
  gridTip: { fontSize: 10, color: C.textMuted, textAlign: 'center', lineHeight: 14 },
  aiHint: {
    backgroundColor: '#FFF7E0', borderRadius: 10, padding: 14,
    borderWidth: 1, borderColor: '#F0DCA0', marginTop: 8, marginBottom: 16,
  },
  aiHintTitle: { fontSize: 12, fontWeight: '700', color: C.goldDark, marginBottom: 6 },
  aiHintText: { fontSize: 12, color: C.text, lineHeight: 18 },
  checkboxRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 12, paddingHorizontal: 4, marginBottom: 12,
  },
  checkbox: {
    width: 22, height: 22, borderRadius: 4, marginRight: 10,
    borderWidth: 2, borderColor: C.border,
    backgroundColor: 'transparent',
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxChecked: { borderColor: C.gold, backgroundColor: C.gold },
  checkboxMark: { color: '#FFF', fontWeight: '900', fontSize: 14 },
  checkboxLabel: { fontSize: 13, color: C.textMuted },
  cta: {
    backgroundColor: C.text, paddingVertical: 14, borderRadius: 10,
    alignItems: 'center', borderWidth: 1, borderColor: C.gold,
  },
  ctaText: { fontSize: 13, fontWeight: '700', color: C.gold, letterSpacing: 0.5 },
});
